import type { RefObject } from "react";
import {
  Building2,
  ExternalLink, Link2, Loader2, Plus, Settings2, X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import SuppliersSelectionDrawer from "@/features/suppliers/components/SuppliersSelectionDrawer/SuppliersSelectionDrawer";
import BaseDrawer from "@/shared/components/Drawer";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import {
  CREDIT_LOCKED_HINT,
  DEBIT_LOCKED_HINT,
  LOCKED_AMOUNT_INPUT_CLASS,
  formatAmount,
  formatCurrencyField,
  getAmountSideLock,
  normalizeSearchText,
  normalizeCurrencyTyping,
} from "./utils";
import type { EditableAllocationState } from "./utils";
import type { LedgerAllocation, LedgerAllocationOperationType, Settlement } from "../../types/accounting.types";

type SimpleAllocationEditorProps = {
  open: boolean
  onClose: () => void
  selectedAllocation: LedgerAllocation | null
  simpleEditorRow: EditableAllocationState | null
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  isSimpleEditorBusy: boolean
  isLoadingOperationTypes: boolean
  isCreateOperationTypePending: boolean
  // Category search
  operationTypeSearch: string
  setOperationTypeSearch: (value: string) => void
  simpleCategoryPopoverOpen: boolean
  setSimpleCategoryPopoverOpen: (open: boolean) => void
  simpleCategoryPopoverSearch: string
  setSimpleCategoryPopoverSearch: (value: string) => void
  markSimpleCategorySearchBusy: () => void
  simpleCategoryContainerRef: RefObject<HTMLDivElement | null>
  operationTypes: LedgerAllocationOperationType[]
  // Supplier search
  supplierSearch: string
  setSupplierSearch: (value: string) => void
  suppliers: { id: string, name: string }[]
  isLoadingSuppliers: boolean
  selectedSupplierId: string
  onSelectSupplier: (id: string, name: string) => void
  onClearSupplier: () => void
  isSupplierPopoverOpen: boolean
  setIsSupplierPopoverOpen: (open: boolean) => void
  supplierContainerRef: RefObject<HTMLDivElement | null>
  // Actions
  onSave: () => void
  onFindOrCreateOperationType: () => void
  onOpenCategoryManager: () => void
  onOpenRulesManager: () => void
  onOpenSettlement?: (settlementId: string) => void
  // Attach to settlement
  movementSettlements?: Settlement[]
  onAttachToSettlement?: (allocationId: string, settlementId: string) => void
  isAttachingToSettlement?: boolean
};

const SimpleAllocationEditor = ({
  open,
  onClose,
  selectedAllocation,
  simpleEditorRow,
  setField,
  isSimpleEditorBusy,
  isLoadingOperationTypes,
  isCreateOperationTypePending,
  operationTypeSearch,
  setOperationTypeSearch,
  simpleCategoryPopoverOpen,
  setSimpleCategoryPopoverOpen,
  simpleCategoryPopoverSearch,
  setSimpleCategoryPopoverSearch,
  markSimpleCategorySearchBusy,
  simpleCategoryContainerRef,
  operationTypes,
  supplierSearch,
  setSupplierSearch,
  suppliers,
  isLoadingSuppliers,
  selectedSupplierId,
  onSelectSupplier,
  onClearSupplier,
  isSupplierPopoverOpen,
  setIsSupplierPopoverOpen,
  supplierContainerRef,
  onSave,
  onFindOrCreateOperationType,
  onOpenCategoryManager,
  onOpenSettlement,
  movementSettlements = [],
  onAttachToSettlement,
  isAttachingToSettlement = false,
}: SimpleAllocationEditorProps) => {
  const { isDebitLocked, isCreditLocked } = getAmountSideLock(simpleEditorRow?.debit, simpleEditorRow?.credit);
  const [settlementPickerOpen, setSettlementPickerOpen] = useState(false);
  const [settlementPickerSearch, setSettlementPickerSearch] = useState("");
  const [isSuppliersDrawerOpen, setIsSuppliersDrawerOpen] = useState(false);

  const filteredSettlements = useMemo(() => {
    if (!settlementPickerSearch.trim()) return movementSettlements;
    const q = normalizeSearchText(settlementPickerSearch);
    return movementSettlements.filter((s) =>
      normalizeSearchText(`${s.number ?? ""} ${s.issuer ?? ""} ${s.paymentMethod ?? ""}`).includes(q),
    );
  }, [movementSettlements, settlementPickerSearch]);

  const handleAttach = useCallback((settlementId: string) => {
    if (!selectedAllocation || !onAttachToSettlement) return;
    onAttachToSettlement(selectedAllocation.id, settlementId);
    setSettlementPickerOpen(false);
    setSettlementPickerSearch("");
  }, [selectedAllocation, onAttachToSettlement]);
  const filteredSimpleCategoryOptions = useMemo(() => {
    const query = normalizeSearchText(simpleCategoryPopoverSearch);
    if (!query) return operationTypes;
    return operationTypes.filter((operationType) => normalizeSearchText(operationType.name).includes(query));
  }, [operationTypes, simpleCategoryPopoverSearch]);

  const exactSimpleCategoryPopoverMatch = useMemo(() => {
    const query = normalizeSearchText(simpleCategoryPopoverSearch);
    if (!query) return null;
    return operationTypes.find((operationType) => normalizeSearchText(operationType.name) === query) ?? null;
  }, [operationTypes, simpleCategoryPopoverSearch]);

  return (
    <>
      <BaseDrawer
        drawerId="DRW-SIMPLE"
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        isFirstLevel={false}
        onBack={onClose}
        width="893px"
        zIndex={65}
        bodyClassName="[&_*]:select-text"
        title="🧾 Écriture simple"
        description="Affectez une catégorie comptable à cette écriture."
      >
        {selectedAllocation?.settlementId && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 mb-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[12px] font-medium text-indigo-900">
                Règlement associé
              </div>
              {onOpenSettlement && (
                <button
                  type="button"
                  onClick={() => onOpenSettlement(selectedAllocation.settlementId!)}
                  className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                >
                  Voir le règlement
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              {selectedAllocation.settlementNumber && (
                <div>
                  <span className="text-indigo-700">N° :</span>
                  {" "}
                  <span className="font-medium text-indigo-950">{selectedAllocation.settlementNumber}</span>
                </div>
              )}
              {selectedAllocation.settlementDate && (
                <div>
                  <span className="text-indigo-700">Date :</span>
                  {" "}
                  <span className="font-medium text-indigo-950">{selectedAllocation.settlementDate}</span>
                </div>
              )}
              {selectedAllocation.settlementIssuer && (
                <div>
                  <span className="text-indigo-700">Émetteur :</span>
                  {" "}
                  <span className="font-medium text-indigo-950">{selectedAllocation.settlementIssuer}</span>
                </div>
              )}
              {selectedAllocation.settlementPaymentMethod && (
                <div>
                  <span className="text-indigo-700">Support :</span>
                  {" "}
                  <span className="font-medium text-indigo-950">{selectedAllocation.settlementPaymentMethod}</span>
                </div>
              )}
              {selectedAllocation.settlementDeferredDate && (
                <div>
                  <span className="text-indigo-700">Date différée :</span>
                  {" "}
                  <span className="font-medium text-indigo-950">{selectedAllocation.settlementDeferredDate}</span>
                </div>
              )}
            </div>
            {(selectedAllocation.settlementAmount !== null || selectedAllocation.settlementRemainingAmount !== null) && (
              <div className="border-t border-indigo-200 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                {selectedAllocation.settlementAmount !== null && (
                  <div>
                    <span className="text-indigo-700">Montant :</span>
                    {" "}
                    <span className="font-medium tabular-nums text-indigo-950">
                      {formatAmount(selectedAllocation.settlementAmount)}
                      {" "}
                      €
                    </span>
                  </div>
                )}
                {selectedAllocation.settlementRemainingAmount !== null && (
                  <div>
                    <span className="text-indigo-700">Solde :</span>
                    {" "}
                    <span className="font-medium tabular-nums text-indigo-950">
                      {formatAmount(selectedAllocation.settlementRemainingAmount)}
                      {" "}
                      €
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!selectedAllocation?.settlementId && movementSettlements.length > 0 && onAttachToSettlement && (
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 mb-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[12px] font-medium text-slate-700 flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5 text-slate-500" />
                Attacher au règlement
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-6 px-2 text-[10px] gap-1"
                onClick={() => setSettlementPickerOpen(!settlementPickerOpen)}
                disabled={isAttachingToSettlement || isSimpleEditorBusy}
              >
                {settlementPickerOpen ? "Fermer" : "Choisir un règlement"}
              </Button>
            </div>
            {settlementPickerOpen && (
              <div className="space-y-1.5">
                <Input
                  value={settlementPickerSearch}
                  onChange={(e) => setSettlementPickerSearch(e.target.value)}
                  placeholder="Rechercher par numéro, émetteur..."
                  className="h-7 !text-[11px] md:!text-[11px]"
                />
                <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                  {filteredSettlements.length === 0 && (
                    <div className="px-2 py-2 text-[11px] text-slate-500">Aucun règlement trouvé</div>
                  )}
                  {filteredSettlements.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="w-full text-left px-2.5 py-2 hover:bg-indigo-50 transition-colors disabled:opacity-50"
                      onClick={() => handleAttach(s.id)}
                      disabled={isAttachingToSettlement}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] font-medium text-slate-800 truncate">
                          N°
                          {" "}
                          {s.number || "-"}
                          {s.paymentMethod && (
                            <span className="ml-1.5 text-slate-500">
                              ·
                              {s.paymentMethod}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] tabular-nums text-slate-600 shrink-0">
                          {s.amount !== null
                            ? formatAmount(s.amount) + " €"
                            : "-"}
                        </div>
                      </div>
                      {s.issuer && (
                        <div className="text-[10px] text-slate-500 truncate">{s.issuer}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="rounded-md border bg-white p-3 mb-4 space-y-3 [&_label]:!text-[12px] [&_label]:font-normal [&_label]:leading-tight [&_input]:!text-[12px] [&_input]:md:!text-[12px] [&_input]:leading-tight">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
            <div className="space-y-1">
              <label className="text-[12px] leading-tight text-slate-600">Date</label>
              <Input
                type="date"
                min={ACCOUNTING_DATE_BOUNDS.min}
                max={ACCOUNTING_DATE_BOUNDS.max}
                value={simpleEditorRow?.date ?? ""}
                onChange={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "date", event.target.value, selectedAllocation);
                }}
                className="h-8 !text-[12px] md:!text-[12px] leading-tight"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[12px] leading-tight text-slate-600">Type budget</label>
              <select
                title="Type budget"
                className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]"
                value={simpleEditorRow?.budgetType || "ASC"}
                onChange={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "budgetType", event.target.value, selectedAllocation);
                  setField(selectedAllocation.id, "operationTypeId", "", selectedAllocation);
                  setField(selectedAllocation.id, "operationType", "", selectedAllocation);
                  setOperationTypeSearch("");
                }}
                disabled={isSimpleEditorBusy}
              >
                <option value="ASC">ASC</option>
                <option value="AEP">AEP</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[12px] leading-tight text-slate-600">Débit</label>
              <Input
                aria-label="Débit"
                inputMode="decimal"
                value={simpleEditorRow?.debit ?? ""}
                onChange={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "debit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                }}
                onBlur={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "debit", formatCurrencyField(event.target.value), selectedAllocation);
                }}
                className={`h-8 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                placeholder="0,00"
                readOnly={isDebitLocked}
                aria-disabled={isDebitLocked}
                title={isDebitLocked ? DEBIT_LOCKED_HINT : undefined}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[12px] leading-tight text-slate-600">Crédit</label>
              <Input
                aria-label="Crédit"
                inputMode="decimal"
                value={simpleEditorRow?.credit ?? ""}
                onChange={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "credit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                }}
                onBlur={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "credit", formatCurrencyField(event.target.value), selectedAllocation);
                }}
                className={`h-8 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                placeholder="0,00"
                readOnly={isCreditLocked}
                aria-disabled={isCreditLocked}
                title={isCreditLocked ? CREDIT_LOCKED_HINT : undefined}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[12px] leading-tight text-slate-600">Libellé</label>
            <Input
              value={simpleEditorRow?.description ?? ""}
              onChange={(event) => {
                if (!selectedAllocation) return;
                setField(selectedAllocation.id, "description", event.target.value, selectedAllocation);
              }}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight"
            />
          </div>

          {/* Fournisseur */}
          <div className=" space-y-1.5">
            <label className="text-[12px] leading-tight text-slate-600">
              Fournisseur
              {isLoadingSuppliers && <Loader2 className="inline h-3 w-3 ml-1 animate-spin" />}
            </label>
            <div ref={supplierContainerRef} className="relative">
              <Input
                value={supplierSearch}
                placeholder="Rechercher un fournisseur"
                onFocus={() => setIsSupplierPopoverOpen(true)}
                onClick={() => setIsSupplierPopoverOpen(true)}
                onChange={(event) => {
                  setSupplierSearch(event.target.value);
                  setIsSupplierPopoverOpen(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setIsSupplierPopoverOpen(false);
                }}
                className="h-8 !text-[12px] md:!text-[12px] leading-tight"
                disabled={isSimpleEditorBusy}
              />
              {isSupplierPopoverOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-md p-1">
                  <div className="max-h-48 overflow-y-auto overscroll-contain">
                    {suppliers.length === 0 && !isLoadingSuppliers && (
                      <div className="px-2 py-1.5 text-[12px] text-slate-500">Aucun fournisseur trouvé</div>
                    )}
                    {suppliers.map((supplier) => (
                      <button
                        key={supplier.id}
                        type="button"
                        className="w-full text-left px-2 py-1.5 text-[12px] rounded hover:bg-slate-100"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => onSelectSupplier(supplier.id, supplier.name)}
                      >
                        {supplier.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {selectedSupplierId && (
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] text-emerald-900">
                  <span className="truncate">{supplierSearch || "Fournisseur sélectionné"}</span>
                  <button
                    type="button"
                    aria-label="Retirer le fournisseur"
                    className="inline-flex items-center text-emerald-700 hover:text-emerald-900"
                    onClick={onClearSupplier}
                    disabled={isSimpleEditorBusy}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              className="h-8 text-[12px]"
              onClick={() => setIsSuppliersDrawerOpen(true)}
              disabled={isSimpleEditorBusy}
            >
              <Building2 className="h-3.5 w-3.5 mr-1" />
              Gérer les fournisseurs
            </Button>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2 space-y-1.5">
            <label className="text-[12px] font-medium text-slate-700 block" htmlFor="simple-entry-category">
              Catégorie
            </label>
            <div className="space-y-1.5">
              <div ref={simpleCategoryContainerRef} className="relative">
                <Input
                  id="simple-entry-category"
                  value={operationTypeSearch}
                  onFocus={() => setSimpleCategoryPopoverOpen(true)}
                  onClick={() => setSimpleCategoryPopoverOpen(true)}
                  onChange={(event) => {
                    setOperationTypeSearch(event.target.value);
                    setSimpleCategoryPopoverSearch(event.target.value);
                    markSimpleCategorySearchBusy();
                    setSimpleCategoryPopoverOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      onFindOrCreateOperationType();
                    }
                    if (event.key === "Escape") {
                      setSimpleCategoryPopoverOpen(false);
                    }
                    if (event.key === "ArrowDown") {
                      setSimpleCategoryPopoverOpen(true);
                    }
                  }}
                  placeholder="Rechercher ou créer une catégorie"
                  className="h-8 !text-[12px] md:!text-[12px] leading-tight pr-8"
                  disabled={isSimpleEditorBusy || isLoadingOperationTypes}
                />
                <div className="absolute inset-y-0 right-2 flex items-center">
                  {(isLoadingOperationTypes || isCreateOperationTypePending)
                    ? (
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    )
                    : (
                      <span className="text-xs text-slate-400">▼</span>
                    )}
                </div>
                {simpleCategoryPopoverOpen && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-md p-1">
                    <div className="max-h-48 overflow-y-auto overscroll-contain">
                      {filteredSimpleCategoryOptions.length === 0 && (
                        <div className="px-2 py-1.5 text-[12px] text-slate-500">Aucune catégorie trouvée</div>
                      )}
                      {filteredSimpleCategoryOptions.map((operationType) => (
                        <button
                          key={operationType.id}
                          type="button"
                          className="w-full text-left px-2 py-1.5 text-[12px] rounded hover:bg-slate-100"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "operationTypeId", operationType.id, selectedAllocation);
                            setField(selectedAllocation.id, "operationType", operationType.name, selectedAllocation);
                            setOperationTypeSearch(operationType.name);
                            setSimpleCategoryPopoverSearch(operationType.name);
                            setSimpleCategoryPopoverOpen(false);
                          }}
                        >
                          {operationType.name}
                        </button>
                      ))}
                    </div>
                    {Boolean(simpleCategoryPopoverSearch.trim()) && !exactSimpleCategoryPopoverMatch && (
                      <div className="border-t mt-0.5 pt-0.5">
                        <button
                          type="button"
                          className="w-full flex items-center gap-1 px-2 py-1.5 text-[12px] rounded hover:bg-slate-100 text-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setOperationTypeSearch(simpleCategoryPopoverSearch.trim());
                            onFindOrCreateOperationType();
                          }}
                          disabled={isCreateOperationTypePending}
                        >
                          {isCreateOperationTypePending
                            ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Création...
                              </>
                            )
                            : (
                              <>
                                <Plus className="h-3.5 w-3.5" />
                                Créer "
                                {simpleCategoryPopoverSearch.trim()}
                                "
                              </>
                            )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="text-[12px] leading-tight text-slate-500">
                Tape un nom puis `Entrée` pour sélectionner ou créer automatiquement.
              </div>
              {Boolean(simpleEditorRow?.operationType || simpleEditorRow?.operationTypeId) && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[12px] text-sky-900">
                    <span className="truncate">{simpleEditorRow?.operationType || "Catégorie associée"}</span>
                    <button
                      type="button"
                      className="inline-flex items-center text-sky-700 hover:text-sky-900"
                      onClick={() => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "operationTypeId", "", selectedAllocation);
                        setField(selectedAllocation.id, "operationType", "", selectedAllocation);
                        setOperationTypeSearch("");
                        setSimpleCategoryPopoverSearch("");
                      }}
                      aria-label="Supprimer l'association"
                      disabled={isSimpleEditorBusy}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-8 text-[12px]"
                  onClick={onOpenCategoryManager}
                  disabled={isSimpleEditorBusy}
                >
                  <Settings2 className="h-3.5 w-3.5 mr-1" />
                  Gérer les catégories
                </Button>
                {/* Rules manager button hidden — feature not ready for end users */}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSimpleEditorBusy}
              className="h-8 text-[12px]"
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="success"
              onClick={onSave}
              disabled={isSimpleEditorBusy}
              className="h-8 text-[12px]"
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </BaseDrawer>
      <SuppliersSelectionDrawer
        open={isSuppliersDrawerOpen}
        onOpenChange={setIsSuppliersDrawerOpen}
        onSupplierSelected={(supplier) => {
          onSelectSupplier(supplier.id, supplier.name);
          setIsSuppliersDrawerOpen(false);
        }}
        width="893px"
        zIndex={65}
      />
    </>
  );
};

export default SimpleAllocationEditor;
